'use client';

import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {debounce} from '@/lib/debounce';
import * as m from "@/paraglide/messages.js";
import {Cart as ApiCart, CartDiscount, Coupon, CustomerExtraInformation, getProduct, isReserveProductRejection, OrderedList, Payment, PaymentMethod, PaymentResponse, PaymentStatus, ProductQuantity, ProductSettings, ProductTimeSlot, ReserveProductError, TicketApi} from '@/lib/TicketApi'
import { Money } from '@/lib/money';
import {ulid} from "ulidx";
import { useShop } from './ShopProvider';
import { calculateFee } from './../lib/calculateFee';
import { eventEmitter } from '@/lib/eventEmitter';
import { useTracking } from '@/tracking/context';
import { calculateFees } from './../lib/calculateFee';
import { useLanguage } from './LanguageProvider';
import { useLeaveCart } from '@/hooks/useLeaveCart';

// Define the context
const CartContext = createContext<CartContextType|null>(null);

export interface FormComponent {
    type: string;
    key: string;
}

/**
 * FAS-1053 — a cart holds LINES, not one entry per product: the same product can sit in the cart
 * several times, once per time slot or per chosen top-up amount. `lineId` is a ulid we mint and
 * keep for the lifetime of the line; the API keys its answers back on it.
 */
export interface CartLine {
    lineId: string;
    productId: string;
    quantity: number;
    settings?: ProductSettings;
    /** FAS-1622 — see `ProductQuantity`; absent on an older API response or a line we haven't sent yet. */
    unitPriceInCents?: number;
    originalUnitPriceInCents?: number;
    discountPerUnitInCents?: number;
    currency?: string;
}

/** Legacy carts (and any response written before FAS-1053) echo no lineId — fall back to the product id. */
const toCartLines = (products: ProductQuantity[]): CartLine[] =>
    products.map(p => ({...p, lineId: p.lineId ?? p.productId}));

interface Cart {
    id: string;
    products: CartLine[];
    form: OrderedList<FormComponent>,
    answers: object;
    errors: string[]|string[][];
    payments: {[paymentId: string]: Payment;}
    isCheckedOut: boolean;
    pendingPayment: string|null;
    orderId: string|null;
    appliedCouponCode: Coupon|null;
    checkoutAtEventId: string|null;
    /** FAS-1622 — null when no coupon discount applies to this cart. */
    discount: CartDiscount|null;
}

export interface ProductSelectionWithPrice {
    /** The cart line this price belongs to; the aggregate from `totalForProduct` has none. */
    lineId?: string;
    productId: string;
    settings?: ProductSettings;
    /** Resolved from the shop config for a line that bought a time slot. */
    timeSlot?: ProductTimeSlot|null;
    quantity: number;
    name: string;
    unit: Money;
    unitInclFee: Money;
    total: Money;
    feeUnit: Money|null;
    feeTotal: Money|null;
    totalInclFee: Money;
    /** FAS-1622 — the per-unit price before any coupon discount; equal to `unit` when nothing is discounted. */
    originalUnit: Money;
    /** FAS-1622 — `unit` subtracted from `originalUnit`; zero when nothing is discounted. */
    discountPerUnit: Money;
    /** FL-326 — a coupon made this line free and the campaign discounts the fee too: no service fee, as on the server. */
    feeWaived: boolean;
}

interface CartContextType {
    cart: Cart | null;
    loading: boolean;
    ticketApiReady: boolean;
    /**
     * Sets the quantity of ONE cart line. Which line: the one named by `lineId` when given, else
     * the line holding `settings.timeSlotId`, else the product's most recent line. A line that
     * doesn't exist yet is created (unless the quantity is zero), so a second slot or a second
     * top-up amount of the same product becomes a second line instead of overwriting the first.
     */
    setProductQuantity: (productId: string, quantity: number, settings?: ProductSettings, lineId?: string) => void;
    applyCoupon: (discountCode: string) => Promise<string|true>;
    removeCoupon: () => Promise<void>;
    /** Total quantity of a product across all of its lines. */
    selectedQuantity: (productId: string) => number;
    /** Quantity on the line that bought this time slot, 0 when the slot isn't in the cart. */
    selectedQuantityForSlot: (productId: string, timeSlotId: string) => number;
    lineQuantity: (lineId: string) => number;
    /** This product's lines, in cart order. */
    productLines: (productId: string) => CartLine[];
    totalForProduct: (productId: string) => null|ProductSelectionWithPrice;
    error: string;
    costOverview: () => CostOverview;
    productError: (productId: string) => string|null;
    setStep: (step: CartStep) => void;
    cartStep: CartStep,
    submitFormAnswers: () => Promise<ApiCart | null>;
    setFormValid: (valid: boolean) => void;
    formIsValid: boolean;
    setFormState: (state: object) => void;
    formState: object|null;
    setPaymentMethod: (paymentMethod: string) => void;
    paymentMethod: string|null;
    startCheckout: (eventId: string, customer?: CustomerExtraInformation|null, preferRedirect?: boolean) => Promise<PaymentResponse>;
    // Called when an embedded (PAY.PARTS) payment completes: reports the pay.nl order id as a
    // fallback for a lost exchange, then re-reads the cart so the pending-payment UI takes over in
    // place — no full page load to the return URL.
    completeEmbeddedPayment: (paymentId: string, orderId: string|null, token: string) => Promise<void>;
    currentPayment: () => Payment | null;
    clearServerError: (key: string) => void;
    serverErrors: Record<string, string[]>;
    isProductQuantityIncreaseDisabled: (productId: string) => boolean;
    isShopUnlocked: (shopSlug: string) => boolean;
    isProductQuantityIncreaseDisabledByShopUnlock: (productId: string) => boolean;
    getShopUnlockProductLimit: (productId: string) => number | null;
    getCartState: () => 'loading' | 'loaded' | 'no-cart';
}

interface CartProviderProps {
    children: React.ReactNode,
}

interface CostOverview {
    total: Money;
    totalExPaymentFee: Money;
    products: ProductSelectionWithPrice[];
    fee: { name: string; total: Money } | null;
    paymentFee: { name: string; total: Money } | null;
    /** FAS-1622 — the coupon discount already folded into `total`; null when none applies. */
    discount: { code: string|null; total: Money } | null;
}

export enum CartStep {
    Tickets = 'tickets',
    Order = 'Order',
    Checkout = 'checkout',
    PaymentReturn = 'payment-return',
}

export const CartProvider: React.FC<CartProviderProps> = ({children}) => {
    const [ticketApi, setTicketApi] = useState<TicketApi|null>(null);
    const [cart, setCart] = useState<Cart|null>(null);
    const cartRef = useRef<Cart|null>(null);
    const previousEventSlugRef = useRef<string|undefined>(undefined);
    const [paymentMethod, setPaymentMethod] = useState<string|null>(null);
    const [serverErrors, setServerErrors] = useState<Record<string, string[]>>({});

    const {shop, shopSlug, baseUrl, selectedEventSlug, setSelectedEventSlug} = useShop();
    const { trackEvent } = useTracking();
    const { currentLanguage } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [loadingFromStorage, setLoadingFromStorage] = useState(true);

    const lastReportedStep = useRef(CartStep.Tickets);

    const [error, setError] = useState('');

    const [step, setStepInternal] = useState(CartStep.Tickets)
    const setStep = useCallback((newStep: CartStep) => {
        setStepInternal(newStep);
        eventEmitter.emit('CART_STEP_CHANGE', { step: newStep });
    }, []);

    const [productErrors, setProductErrors] = useState<{[productId: string]: string}>({});

    const [formState, setFormState] = useState<object|null>(null);
    const [formValid, setFormValid] = useState<boolean>(false);

    const toTrackingAmount = useCallback((amount: number, currency: string) => {
        return Money.fromInteger(amount, currency).toDecimal();
    }, []);

    const getTrackingProductPrice = useCallback((productId: string, settings?: { amount?: number; currency?: string }) => {
        const product = getProduct(shop, productId);
        const currency = settings?.currency ?? product?.price.currency ?? shop.defaultCurrency;
        const amount = settings?.amount ?? product?.price.amount ?? 0;

        return {
            currency,
            value: toTrackingAmount(amount, currency),
        };
    }, [shop, toTrackingAmount]);

    // Clear cart when switching to a different event
    // in the future we should support multiple events in the same cart
    // for now it gives issues on the order page
    useEffect(() => {
        // Only clear if we're switching between actual events (not when going to/from index)
        if (previousEventSlugRef.current !== undefined && 
            selectedEventSlug !== undefined && // only when switching TO an event
            selectedEventSlug !== previousEventSlugRef.current && 
            cart !== null && 
            cart.pendingPayment === null && 
            cart.isCheckedOut === false && 
            cart.products.length > 0) {
                resetCart();
        }

        if(selectedEventSlug !== undefined){
            previousEventSlugRef.current = selectedEventSlug;
        }
    }, [selectedEventSlug]);

    const handleFormStateChange = (state: object) => {
        // Clear server errors for any fields that are being changed
        Object.keys(state).forEach(key => {
            if (serverErrors[key]) {
                clearServerError(key);
            }
        });
        setFormState(state);
    };

    useEffect(() => {
        if(typeof window === 'undefined'){
            return;
        }
        const searchParams = new URLSearchParams(window.location.search);
        const cartIdFromUrl = searchParams.get('cart_id');
        if (cartIdFromUrl && typeof cartIdFromUrl === 'string') {
            fetchCart(cartIdFromUrl);
            setStep(CartStep.Checkout);
        }
    }, [ticketApi]);

    useEffect(() => {
        if (lastReportedStep.current === step) {
            return;
        }

        trackEvent({
            type: 'PAGE_VIEW',
            payload: {
                shopId: shopSlug,
                path: window.location.pathname + "#" + step,
                title: step,
                params: {
                    innerRouter: step,
                },
            },
        });

        lastReportedStep.current = step;

    
        // Trigger iframe resize after step change
        // this is for pages that use eventix iframe integration
        setTimeout(() => {
            // @ts-expect-error defined in iframeResizer.contentWindow.min.js
            if (window.parentIFrame) {
                // @ts-expect-error defined in iframeResizer.contentWindow.min.js
                window.parentIFrame.size();
            }
        }, 100);

    }, [step, shopSlug, cart?.id, trackEvent]);

    useEffect(() => {
        cartRef.current = cart;
    }, [cart]);

    useEffect(() => {
       if(shop.paymentMethods.length > 0){
            setPaymentMethod(shop.paymentMethods[0].id);
       }
    }, [shop]);

    // Handle leave-cart functionality
    const hasReservedProducts = cart !== null && 
        cart.products.length > 0 && 
        !cart.isCheckedOut && 
        cart.pendingPayment === null;
    
    useLeaveCart({
        ticketApi,
        cartId: cart?.id || null,
        hasReservedProducts
    });

    // Function to fetch order
    const fetchCart = async (cartId: string, inBackground: boolean = false, pageLoadFetch: boolean = false): Promise<ApiCart|null> => {
        if(ticketApi === null){
            return null;
        }
        if(!inBackground){
            setLoading(true);
        }
        try {
            const cart: ApiCart = await ticketApi.loadCart(cartId, currentLanguage);
            updateCart(cart);

            if(pageLoadFetch && Object.keys(cart.payments).length > 0){
                setStep(CartStep.Checkout);
                if (cart.checkoutAtEventId && shop) {
                    const event = shop.events.find(event => event.eventId === cart.checkoutAtEventId);
                    if (event) {
                        console.log('open event slug', event.slug);
                        window.dispatchEvent(new CustomEvent('fastlane_openWidget', {
                            detail: {
                                shopSlug,
                                eventSlug: event.slug
                            }
                        }));
                    }
                } else {
                    console.log('open widget without event slug');
                    window.dispatchEvent(new Event('fastlane_openWidget'));
                }
            }
            return cart;
        } catch (error) {
            console.error('Failed to load cart', error);
            // unset order if it fails to load
            if(!inBackground){
                resetCart();
                setLoading(false);
                setLoadingFromStorage(false);
                setStep(CartStep.Tickets);
                return null;
            }
        } finally {
            if(!inBackground){
                setLoading(false);
            }
        }
        return null;
    };

    const updateCart = (cart: ApiCart) => {
        if(cart.timedOut){
            resetCart();
            return;
        }

        // If we have a checkoutAtEventId, find and set the corresponding event slug
        if (cart.checkoutAtEventId && shop) {
            const event = shop.events.find(event => event.eventId === cart.checkoutAtEventId);
            if (event) {
                setSelectedEventSlug(event.slug);
            }
        }

        setCart({
            id: cart.cartId,
            products: toCartLines(cart.products),
            form: cart.form,
            answers: cart.answers,
            errors: cart.errors,
            payments: cart.payments,
            isCheckedOut: cart.isCheckedOut,
            pendingPayment: cart.pendingPayment,
            orderId: cart.orderId,
            appliedCouponCode: cart.appliedCouponCode,
            checkoutAtEventId: cart.checkoutAtEventId,
            discount: cart.discount ?? null,
        });

        // Update localStorage with the cart ID from the API response
        localStorage.setItem(`cart-${shopSlug}`, cart.cartId);

        // Update server errors state
        if (typeof cart.errors === 'object' && !Array.isArray(cart.errors)) {
            setServerErrors(cart.errors as Record<string, string[]>);
        } else {
            setServerErrors({});
        }
        
        // todo, only emit if coupon is applied and products have changed since last time
        eventEmitter.emit('RELOAD_SHOP_CONFIG', {
            shopSlug: shopSlug,
            unlockedProductIds: (cart.appliedCouponCode?.unlockProducts?.products ?? []).map((p: { productId: string }) => p.productId),
        });
    }

    useEffect(() => {
        if (cart?.pendingPayment) {
            const startTime = Date.now();
            const interval = setInterval(() => {
                if (Date.now() - startTime > 45000) { // 45 seconds
                    clearInterval(interval);
                    cancelPayment(); // Call the retry method
                } else {
                    fetchCart(cart.id, true);
                }
            }, 3000);

            return () => clearInterval(interval);
        }
    }, [cart?.pendingPayment]);

    const cancelPayment = async () => {
        if(ticketApi === null || cart === null || cart.pendingPayment === null){
            return;
        }
        await ticketApi.forceCancelPendingPayment(cart.id, cart.pendingPayment);
        // wait for cart to be updated
        const checkCartUpdate = async () => {
            const newCart = await fetchCart(cart.id, true);
            if (newCart === null || newCart.pendingPayment === null) {
                return;
            }
            setTimeout(checkCartUpdate, 5000);
        };

        checkCartUpdate();
    };

    useEffect(() => {
        if(cart === null){
            return;
        }

        if (cart.pendingPayment) {
            setPaymentMethod(cart.payments[cart.pendingPayment].methodId);
            setStep(CartStep.Checkout);

            // open widget with event slug if available
            if (cart.checkoutAtEventId && shop) {
                const event = shop.events.find(event => event.eventId === cart.checkoutAtEventId);
                if (event) {
                    console.log('open widget with event slug', event.slug);
                    window.dispatchEvent(new CustomEvent('fastlane_openWidget', {
                        detail: {
                            shopSlug,
                            eventSlug: event.slug
                        }
                    }));
                }
            } else {
                console.log('open widget without event slug');
                window.dispatchEvent(new Event('fastlane_openWidget'));
            }
        }

        if(cart.isCheckedOut){
            // Guard: Only send purchase event if not already sent for this cart
            const purchaseEventKey = `pe-${cart.id}`;
            if (!localStorage.getItem(purchaseEventKey)) {
                trackEvent({
                    type: 'PURCHASE',
                    payload: {
                        shopId: shopSlug,
                        currency: costOverview().total.currency,
                        transactionId: cart.id,
                        cartId: cart.id,
                        items: cart.products.map(p => {
                            const trackingPrice = getTrackingProductPrice(p.productId, p.settings);
                            return {
                                productId: p.productId,
                                quantity: p.quantity,
                                price: trackingPrice.value,
                                name: getProduct(shop, p.productId)?.name ?? '',
                            };
                        }),
                        totalValue: costOverview().total.toDecimal(),
                    },
                });
                localStorage.setItem(purchaseEventKey, 'true');
            }
            // remove cart from localStorage
            localStorage.removeItem(`cart-${shopSlug}`);
            // set payment method used to display the right order summary
            const paymentMethod = Object.values(cart.payments).find(p => p.status === PaymentStatus.Paid);
            if(paymentMethod !== undefined){
                setPaymentMethod(paymentMethod.methodId);
            }
            setStep(CartStep.Checkout);
            if (cart.checkoutAtEventId && shop) {
                const event = shop.events.find(event => event.eventId === cart.checkoutAtEventId);
                if (event) {
                    window.dispatchEvent(new CustomEvent('fastlane_openWidget', {
                        detail: {
                            shopSlug,
                            eventSlug: event.slug
                        }
                    }));
                }
            } else {
                window.dispatchEvent(new Event('fastlane_openWidget'));
            }
        }
    }, [cart]);

    // Load orderId from localStorage and fetch order on mount
    useEffect(() => {
        if(shopSlug === undefined || ticketApi === null){
            return;
        }
        const cartId = localStorage.getItem(`cart-${shopSlug}`);
        if (cartId) {
            setLoadingFromStorage(true);
            fetchCart(cartId, false, true).finally(() => {
                setLoadingFromStorage(false);
            });
        } else {
            setLoadingFromStorage(false);
        }
    }, [shopSlug, ticketApi, shop]);

    useEffect(() => {
        setTicketApi(new TicketApi(baseUrl, shopSlug));
    }, [baseUrl, shopSlug]);

    // Track when ticketApi is ready for the Event component
    const ticketApiReady = ticketApi !== null;

    const resetCart = () => {
        setCart(null);
        setStep(CartStep.Tickets);
        setLoadingFromStorage(false);
        localStorage.removeItem(`cart-${shopSlug}`);
    }

    const currentPayment = (): Payment | null => {
        if(cart === null){
            return null;
        }

        // get transaction id from url transactionid
        const searchParams = new URLSearchParams(window.location.search);
        const paymentId = searchParams.get('transactionid');
        if (!paymentId || typeof paymentId !== 'string') {
            return null;
        }

        const payment = cart.payments[paymentId];
        if(payment === undefined){
            return null;
        }

        return payment;
    }

    const reserveProducts = useCallback(debounce(async (trackingEventId?: string) => {
        if(ticketApi === null || cartRef.current === null){
            throw new Error('TicketApi or order is null');
        }
        try {
            setLoading(true);
            const cartOrError = await ticketApi.reserveProducts(cartRef.current?.id, cartRef.current?.products, trackingEventId, currentLanguage);

            if (Object.values(ReserveProductError).includes(cartOrError as ReserveProductError)) {
                if(cartOrError === ReserveProductError.CartTimeout){
                    resetCart();
                    return;
                }
                throw new Error('Failed to reserve products');
            }

            // A 422 means the API refused one product and returned no cart - surface the reason on
            // that product instead of feeding the error body into updateCart.
            if (isReserveProductRejection(cartOrError)) {
                if (cartOrError.product_id) {
                    // FL-581: the tier before this ticket came back on sale (reservations lapsed).
                    const message = cartOrError.error_code === 'previous_tier_available'
                        ? m.product_previous_tier_available()
                        : cartOrError.message ?? cartOrError.error;
                    setProductErrors({...productErrors, [cartOrError.product_id]: message});
                } else {
                    setError(cartOrError.message ?? cartOrError.error);
                }
                setLoading(false);
                return;
            }

            const cart = cartOrError as ApiCart;

            const reservedLines = toCartLines(cart.products);
            cartRef.current?.products.forEach(p => {// check if line is reserved, otherwise set product error
                const reservedProduct = reservedLines.find(rp => rp.lineId === p.lineId);
                if((reservedProduct === undefined || reservedProduct.quantity !== p.quantity) && p.quantity > 0){
                    setProductErrors({...productErrors, [p.productId]: `Could not reserve ${p.quantity} ticket(s) for this product. All inventory is reserved, select a different product or try again later.`});
                }
            })

            // update cart
            updateCart(cart);

            setLoading(false);
        } catch {
            setError('Failed to reserve product');
            // reload cart..
            fetchCart(cartRef.current.id);
        } finally {
            setLoading(false);
        }
    }, 800), [ticketApi, currentLanguage]);  
    // Resolves which cart line a quantity change is about — see setProductQuantity's doc comment.
    const findCartLine = (productId: string, settings?: ProductSettings, lineId?: string): CartLine|undefined => {
        const lines = cart?.products ?? [];
        if (lineId !== undefined) {
            return lines.find(l => l.lineId === lineId);
        }
        if (settings?.timeSlotId !== undefined) {
            return lines.find(l => l.productId === productId && l.settings?.timeSlotId === settings.timeSlotId);
        }
        return [...lines].reverse().find(l => l.productId === productId);
    };

    // Update line quantity immediately and debounce the reservation
    const setProductQuantity = (productId: string, quantity: number, settings?: ProductSettings, lineId?: string) => {
        const existingLine = findCartLine(productId, settings, lineId);
        const targetLineId = existingLine?.lineId ?? lineId ?? ulid();
        const lineQuantityBefore = existingLine?.quantity ?? 0;

        // Removing a line that was never in the cart: nothing to reserve, nothing to track.
        if (quantity <= 0 && existingLine === undefined) {
            return;
        }

        // remove product error if it exists
        if(productErrors[productId] !== undefined){
            const newProductErrors = {...productErrors};
            delete newProductErrors[productId];
            setProductErrors(newProductErrors);
        }

        // Check maxProducts constraint from appliedCouponCode
        if (cart?.appliedCouponCode?.unlockProducts?.maxProducts !== null && cart?.appliedCouponCode?.unlockProducts?.maxProducts !== undefined) {
            const maxProducts = cart.appliedCouponCode.unlockProducts.maxProducts;
            const unlockedProductIds = cart.appliedCouponCode.unlockProducts.products.map(p => p.productId);
            
            // Only apply constraint if this product is unlocked by the coupon
            if (unlockedProductIds.includes(productId)) {
                const currentTotalQuantity = cart.products
                    .filter(p => unlockedProductIds.includes(p.productId))
                    .reduce((total, p) => total + p.quantity, 0);
                
                const newTotalQuantity = currentTotalQuantity - lineQuantityBefore + quantity;
                
                if (newTotalQuantity > maxProducts) {
                    // Don't allow the increase, return early
                    return;
                }
            }
        }

        // Check per-product limit from shop unlock maxQuantityPerProduct — on the product's SUM,
        // the way the API enforces min/max/step across a product's lines.
        const perProductLimit = getShopUnlockProductLimit(productId);
        if (perProductLimit !== null && selectedQuantity(productId) - lineQuantityBefore + quantity > perProductLimit) {
            return;
        }

        const cartId = cart?.id ?? ulid();
        const trackingEventId = `atc_${cartId}_${Date.now()}`;

        let delta = quantity;
        if(cart === null){
            const newCart = {
                id: cartId,
                products: [{lineId: targetLineId, productId, quantity, ...(settings ? { settings } : {})}],
                form: [],
                answers: {},
                errors: [],
                payments: {},
                isCheckedOut: false,
                pendingPayment: null,
                orderId: null,
                appliedCouponCode: null,
                checkoutAtEventId: null,
                discount: null,
            };
            setCart(newCart);
            localStorage.setItem(`cart-${shopSlug}`, newCart.id);
        } else {
            delta = quantity - lineQuantityBefore;
            const updatedProducts = existingLine !== undefined
                ? cart.products.map(p => p.lineId === targetLineId
                    ? { ...p, quantity, ...(settings ? { settings } : {}) }
                    : p)
                : [...cart.products, {lineId: targetLineId, productId, quantity, ...(settings ? { settings } : {})}];

            setCart({ ...cart, products: updatedProducts });
        }

        if(delta > 0){
            const trackingPrice = getTrackingProductPrice(productId, settings);
            trackEvent({
                type: 'ADD_TO_CART',
                payload: {
                    shopId: shopSlug,
                    cartId: cartId,
                    productId,
                    name: getProduct(shop, productId)?.name ?? '',
                    price: trackingPrice.value,
                    currency: trackingPrice.currency,
                    quantity: delta,
                    eventId: trackingEventId,
                },
            });
        }
        else {
            const trackingPrice = getTrackingProductPrice(productId, settings);
            trackEvent({
                type: 'REMOVE_FROM_CART',
                payload: {
                    shopId: shopSlug,
                    cartId: cartId,
                    productId,
                    name: getProduct(shop, productId)?.name ?? '',
                    price: trackingPrice.value,
                    currency: trackingPrice.currency,
                    quantity: -delta,
                    eventId: trackingEventId,
                },
            });
        }

        reserveProducts(trackingEventId);
    }

    const applyCoupon = async (discountCode: string): Promise<string|true> => {

        if(ticketApi === null){
            return "failed to apply coupon";
        }
        
        // Check cart state - if loading, wait for it to complete
        const cartState = getCartState();
        if (cartState === 'loading') {
            return "failed to apply coupon";
        }

        // Check if the coupon is already applied to the current cart
        if (cart !== null && cart.appliedCouponCode !== null && cart.appliedCouponCode.code === discountCode) {
            return true; // Coupon is already applied, no need to call API
        }

        const cartId = cart === null ? ulid() : cart.id;
        const newCart = await ticketApi.applyCoupon(cartId, discountCode);
        if(typeof newCart === 'string'){
            return newCart;
        }

        updateCart(newCart);
        
        return true;
    }

    const removeCoupon = async () => {
        if(ticketApi === null || cart === null){
            return;
        }

        // Remove couponcode from URL before reloading
        const url = new URL(window.location.href);
        url.searchParams.delete('couponcode');
        window.history.replaceState({}, document.title, url.toString());

        // for now refresh cart and reload page
        resetCart();
        window.location.reload();
    }

    const productLines = (productId: string): CartLine[] => {
        return cart?.products.filter(p => p.productId === productId) ?? [];
    };

    // Utility to get the selected quantity for a product, summed over its lines
    const selectedQuantity = (productId: string) => {
        return productLines(productId).reduce((total, p) => total + p.quantity, 0);
    };

    const selectedQuantityForSlot = (productId: string, timeSlotId: string) => {
        return productLines(productId)
            .filter(p => p.settings?.timeSlotId === timeSlotId)
            .reduce((total, p) => total + p.quantity, 0);
    };

    const lineQuantity = (lineId: string) => {
        return cart?.products.find(p => p.lineId === lineId)?.quantity ?? 0;
    };

    const totalForLine = (line: CartLine): ProductSelectionWithPrice|null => {
        if(shop === null){
            return null;
        }

        const product = getProduct(shop, line.productId);
        const quantity = line.quantity;
        if(product === undefined){
            return null;
        }

        // Local aliases so the per-product-type pricing below reads unchanged: it is now scoped to
        // one line, which is where the chosen donation/top-up amount lives.
        const productId = line.productId;
        const cartProduct = line;

        let unit = Money.fromInteger(product.price.amount, product.price.currency);
        
        // For donation products, use the selected amount as the unit price
        if (product.type === 'donation' && cartProduct?.settings?.amount && cartProduct?.settings?.currency) {
            unit = Money.fromInteger(cartProduct.settings.amount, cartProduct.settings.currency);
        } else if (product.type === 'donation') {
            const selectedOption = product.donation.options[0]; // Default to first option
            if (selectedOption) {
                unit = Money.fromInteger(selectedOption.amount, selectedOption.currency);
            }
        }
        // For cashcard products, use the selected amount as the unit price
        else if (product.type === 'cashcard' && cartProduct?.settings?.amount && cartProduct?.settings?.currency) {
            unit = Money.fromInteger(cartProduct.settings.amount, cartProduct.settings.currency);
        } else if (product.type === 'cashcard') {
            const selectedOption = product.cashcard.options[0]; // Default to first option
            if (selectedOption) {
                unit = Money.fromInteger(selectedOption.amount, selectedOption.currency);
            }
        }
        // For cashless wallet products, apply priceOverridePercentage if set
        else if (product.type === 'cashlessWallet' && cartProduct?.settings?.amount && cartProduct?.settings?.currency) {
            const balanceAmount = cartProduct.settings.amount;
            const override = product.cashlessWallet.priceOverridePercentage;
            const chargedAmount = override != null ? Math.round(balanceAmount * override / 100) : balanceAmount;
            unit = Money.fromInteger(chargedAmount, cartProduct.settings.currency);
        } else if (product.type === 'cashlessWallet') {
            const selectedOption = product.cashlessWallet.options[0];
            if (selectedOption) {
                const override = product.cashlessWallet.priceOverridePercentage;
                const chargedAmount = override != null ? Math.round(selectedOption.amount * override / 100) : selectedOption.amount;
                unit = Money.fromInteger(chargedAmount, selectedOption.currency);
            }
        }

        // FAS-1622 — the server is the authority on what a line actually costs: a coupon discount
        // is priced there, never derived from the shop's product list. `originalUnit` stays the
        // shop-computed price above (used as the fee base below, and to show a struck-through
        // "was" price) unless the API sent its own.
        let originalUnit = unit;
        if (cartProduct.unitPriceInCents !== undefined) {
            const currency = cartProduct.currency ?? unit.currency;
            unit = Money.fromInteger(cartProduct.unitPriceInCents, currency);
            originalUnit = cartProduct.originalUnitPriceInCents !== undefined
                ? Money.fromInteger(cartProduct.originalUnitPriceInCents, currency)
                : unit;
        }
        const discountPerUnit = originalUnit.subtract(unit);

        const productTotal = unit.multiply(quantity);

        // add fee
        let serviceFee: null | Money = null;
        let serviceFeeUnit: null | Money = null;
        let unitInclFee: Money = unit;
        let totalInclFee = productTotal;
        // FAS-1622 — the fee base mirrors the server: original (pre-discount) price by default,
        // the discounted price only when the applied coupon says the discount applies to the fee.
        const feeBase = cart?.discount?.appliesToServiceFee ? unit : originalUnit;
        // Calculate total service fees for all items
        // FL-326 — a unit the coupon makes free carries no service fee at all (mirrors CartProduct::appliedServiceFees).
        const feeWaived = Boolean(cart?.discount?.appliesToServiceFee) && unit.isZero() && !discountPerUnit.isZero();
        const totalServiceFees = feeWaived ? null : calculateFees(product.serviceFees, feeBase, quantity);
        if (totalServiceFees && !totalServiceFees.isZero()) {
            serviceFee = totalServiceFees;
            serviceFeeUnit = calculateFees(product.serviceFees, feeBase, 1);
            totalInclFee = totalInclFee.add(serviceFee);
            unitInclFee = unit.add(serviceFeeUnit);
        }
        const timeSlot = line.settings?.timeSlotId !== undefined
            ? product.timeSlots?.find(s => s.id === line.settings?.timeSlotId) ?? null
            : null;

        return {lineId: line.lineId, productId, settings: line.settings, timeSlot, quantity, name: product.name, unit, total: productTotal, feeUnit: serviceFeeUnit, feeTotal: serviceFee, totalInclFee, unitInclFee, originalUnit, discountPerUnit, feeWaived};
    }

    /**
     * The product's price across ALL of its lines — what the product card shows next to its
     * stepper. The per-line breakdown (one row per slot / per amount) comes from costOverview,
     * so the summed quantity and totals are the meaningful fields here; `unit`/`unitInclFee` are
     * the first line's and only make sense for a product whose lines share a price.
     */
    const totalForProduct = (productId: string): ProductSelectionWithPrice|null => {
        const lines = productLines(productId);
        if (shop === null || getProduct(shop, productId) === undefined) {
            return null;
        }

        const totals = lines.map(totalForLine).filter(t => t !== null);
        if (totals.length === 0) {
            return totalForLine({lineId: productId, productId, quantity: 0});
        }

        return totals.reduce((sum, t) => ({
            ...sum,
            lineId: undefined,
            settings: undefined,
            timeSlot: null,
            quantity: sum.quantity + t.quantity,
            total: sum.total.add(t.total),
            totalInclFee: sum.totalInclFee.add(t.totalInclFee),
            feeTotal: sum.feeTotal === null ? t.feeTotal : (t.feeTotal === null ? sum.feeTotal : sum.feeTotal.add(t.feeTotal)),
        }));
    }

    const productError = (productId: string): string|null => {
        return productErrors[productId] ?? null;
    }

    const costOverview = (): CostOverview => {
        if(shop === null || cart === null){
            return {
                total: Money.zero(shop?.defaultCurrency ?? 'EUR'),
                totalExPaymentFee: Money.zero(shop?.defaultCurrency ?? 'EUR'),
                products: [],
                fee: null,
                paymentFee: null,
                discount: null,
            };
        }

        let totalExPaymentFee = Money.zero(shop.defaultCurrency);
        let fee = Money.zero(shop.defaultCurrency);

        // One entry per LINE: two slots (or two top-up amounts) of the same product are two rows.
        const products = cart.products.filter(p => p.quantity > 0).map(p => {
            const product = totalForLine(p);
            if(product === null){
                return null;
            }
            totalExPaymentFee = totalExPaymentFee.add(product.total);
            if(product.feeTotal !== null){
                fee = fee.add(product.feeTotal);
            }
            return product;
        }).filter(p => p !== null);

        totalExPaymentFee = totalExPaymentFee.add(fee);

         // add payment fee
         const selectedPaymentMethod: PaymentMethod|undefined = shop.paymentMethods.find(pm => pm.id === paymentMethod);

         let paymentFee = Money.zero(shop.defaultCurrency);

         let total = totalExPaymentFee;

         if(step !== CartStep.Tickets && step !== CartStep.Order && !totalExPaymentFee.isZero()){
            if(selectedPaymentMethod?.fee !== undefined){
            paymentFee = calculateFee(selectedPaymentMethod.fee, total, 1);
            total = total.add(paymentFee);
            }
        }

        // FAS-1622 — already folded into `total` via each line's server-priced `unit`; carried
        // separately only so the UI can label it (code + amount taken off).
        const discount = cart.discount === null ? null : {
            code: cart.discount.code,
            total: Money.fromInteger(cart.discount.totalInCents, cart.discount.currency),
        };

        return {
            total,
            totalExPaymentFee,
            products,
            fee: fee.isZero() ? null : { name: 'Service Fee', total: fee},
            paymentFee: paymentFee.isZero() ? null : { name: selectedPaymentMethod?.fee?.name ?? 'Payment Fee', total: paymentFee},
            discount,
        };
    }

    const submitFormAnswers = async () => {
        if(cart === null){
            throw new Error('Cart is null');
        }

        if (ticketApi === null) {
            throw new Error('TicketApi is null');
        }

        try {
            setLoading(true);
            const result = await ticketApi.setFormAnswers(cart.id, formState ?? {}, currentLanguage);
            updateCart(result);
            setLoading(false);
            return result;
        } catch (err) {
            const error = err as Error;
            console.error('Failed to set form answers:', error);
            setError(error?.message ?? 'Failed to set form answers');
            setLoading(false);
            return null;
        }
    }

    // Pulls the cart again so the UI reflects server-side state that changed without us navigating —
    // specifically an embedded payment completing, after which the cart has a pending payment and
    // the PaymentStatus view should take over.
    const refreshCart = async (): Promise<void> => {
        const currentCartId = cartRef.current?.id;
        if (!currentCartId) {
            return;
        }
        await fetchCart(currentCartId, true);
    };

    const completeEmbeddedPayment = async (paymentId: string, orderId: string|null, token: string): Promise<void> => {
        const currentCartId = cartRef.current?.id;

        // Best effort: the API verifies the order id against pay.nl before believing it, and the
        // signed exchange remains the authoritative path — so a failure here costs nothing and
        // must never keep the buyer staring at a completed payment form.
        if (orderId && ticketApi && currentCartId) {
            try {
                await ticketApi.reportEmbeddedPaymentResult({
                    cartId: currentCartId,
                    paymentId,
                    orderId,
                    token,
                });
            } catch (e) {
                console.warn('Could not report the embedded payment result', e);
            }
        }

        await refreshCart();
    };

    // event id is now passed, in the future it might be retreived from another provider
    const startCheckout = async (eventId: string, customer: CustomerExtraInformation|null = null, preferRedirect: boolean = false): Promise<PaymentResponse> => {
        if(cart === null){
            throw new Error('Cart is null');
        }

        if (ticketApi === null) {
            throw new Error('TicketApi is null');
        }

        const trackingEventId = `begin_checkout_${cart.id}_${Date.now()}`;

        try {
            setLoading(true);

            trackEvent({
                type: 'BEGIN_CHECKOUT',
                payload: {
                    shopId: shopSlug,
                    currency: costOverview().total.currency,
                    cartId: cart.id,
                    eventId: trackingEventId,
                    items: cart.products.map(p => {
                        const trackingPrice = getTrackingProductPrice(p.productId, p.settings);
                        return {
                            productId: p.productId,
                            quantity: p.quantity,
                            price: trackingPrice.value,
                            name: getProduct(shop, p.productId)?.name ?? '',
                        };
                    }),
                    totalValue: costOverview().total.toDecimal(),
                },
            });

            const paymentResponse = await ticketApi.checkout({
                cartId: cart.id,
                eventId: eventId,
                paymentMethodId: paymentMethod,
                redirectUrl: null,
                trackingEventId: trackingEventId,
                languageCode: currentLanguage,
                customer: customer,
                preferRedirect: preferRedirect,
            });

            if(paymentResponse.type === 'no_payment_required') {
                // Start polling for order completion
                const pollInterval = setInterval(async () => {
                    const updatedCart = await ticketApi.loadCart(cart.id);
                    if (updatedCart.orderId) {
                        clearInterval(pollInterval);
                        updateCart(updatedCart);
                        setLoading(false);
                    }
                }, 500); // Poll every .5 seconds

                // Set a timeout to stop polling after a certain time (e.g., 5 minutes)
                setTimeout(() => {
                    clearInterval(pollInterval);
                    if (!cartRef.current?.orderId) {
                        setError('Order processing timed out. Please check your order status later.');
                        setLoading(false);
                    }
                }, 300000); // 5 minutes timeout
                return paymentResponse;
            }

            setLoading(false);
            return paymentResponse;
        } catch (error) {
            setLoading(false);
            
            // Handle coupon error specifically
            if (error instanceof Error) {
                try {
                    const errorData = JSON.parse(error.message);
                    if (errorData.type === 'coupon_error') {
                        setError(errorData.message);
                        return { type: "error" as const, error: errorData.message };
                    }
                } catch {
                    // If parsing fails, it's not a coupon error, so re-throw
                }
            }

            // FAS-1686: a stale checkout (an old tab, a retry) is refused because this cart was
            // already paid. Reload it: a checked-out cart renders the "collect your order" button
            // instead of a pay button that can never work, so there is nothing to report.
            try {
                const refreshedCart = await ticketApi.loadCart(cart.id, currentLanguage);
                updateCart(refreshedCart);
                if (refreshedCart.isCheckedOut) {
                    return { type: "error" as const };
                }
            } catch {
                // Keep the original checkout error below.
            }

            throw error;
        }
    }

    const clearServerError = (key: string) => {
        setServerErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
        });
    };

    const isProductQuantityIncreaseDisabled = (productId: string): boolean => {
        if (!cart?.appliedCouponCode?.unlockProducts?.maxProducts) {
            return false;
        }

        const maxProducts = cart.appliedCouponCode.unlockProducts.maxProducts;
        const unlockedProductIds = cart.appliedCouponCode.unlockProducts.products.map(p => p.productId);
        
        // Only apply constraint if this product is unlocked by the coupon
        if (!unlockedProductIds.includes(productId)) {
            return false;
        }

        const currentTotalQuantity = cart.products
            .filter(p => unlockedProductIds.includes(p.productId))
            .reduce((total, p) => total + p.quantity, 0);
        
        // Check if adding 1 more would exceed the limit
        return currentTotalQuantity >= maxProducts;
    };

    const isShopUnlocked = (shopSlug: string): boolean => {
        if (!cart?.appliedCouponCode?.shopUnlock) {
            return false;
        }
        return cart.appliedCouponCode.shopUnlock.slug.slug === shopSlug;
    };

    const isProductQuantityIncreaseDisabledByShopUnlock = (productId: string): boolean => {
        if (!cart?.appliedCouponCode?.shopUnlock) {
            return false;
        }

        const shopUnlock = cart.appliedCouponCode.shopUnlock;

        // Check per-product limit
        const perProductLimit = getShopUnlockProductLimit(productId);
        if (perProductLimit !== null) {
            const currentQuantity = selectedQuantity(productId);
            if (currentQuantity >= perProductLimit) {
                return true;
            }
        }

        const maxProducts = shopUnlock.maxProducts;

        if (maxProducts === null) {
            return false;
        }

        const productsInMaxProducts = shopUnlock.products_in_max_products;

        // If products_in_max_products is null, all products count towards the limit
        // If it's an array, only products in that array count towards the limit
        const relevantProducts = productsInMaxProducts === null
            ? cart.products
            : cart.products.filter(p => productsInMaxProducts.includes(p.productId));

        const currentTotalQuantity = relevantProducts.reduce((total, p) => total + p.quantity, 0);

        // Check if adding 1 more would exceed the limit
        if (currentTotalQuantity >= maxProducts) {
            // Only disable if this product is relevant to the limit
            if (productsInMaxProducts === null || productsInMaxProducts.includes(productId)) {
                return true;
            }
        }

        return false;
    };

    const getShopUnlockProductLimit = (productId: string): number | null => {
        const maxQuantityPerProduct = cart?.appliedCouponCode?.shopUnlock?.maxQuantityPerProduct;
        if (!maxQuantityPerProduct) {
            return null;
        }

        const entry = maxQuantityPerProduct.find(e => e.productId === productId);
        return entry?.quantity ?? null;
    };

    const getCartState = (): 'loading' | 'loaded' | 'no-cart' => {
        if (loading || loadingFromStorage) {
            return 'loading';
        }
        
        if (cart !== null) {
            return 'loaded';
        }
        
        return 'no-cart';
    };

    const contextValue: CartContextType = {
        cart: cart,
        loading,
        ticketApiReady,
        setProductQuantity,
        selectedQuantity,
        selectedQuantityForSlot,
        lineQuantity,
        productLines,
        totalForProduct,
        error,
        costOverview,
        productError,
        setStep: setStep,
        cartStep: step,
        submitFormAnswers: submitFormAnswers,
        setFormValid: setFormValid,
        formIsValid: formValid,
        setFormState: handleFormStateChange,
        formState: formState,
        setPaymentMethod: setPaymentMethod,
        paymentMethod: paymentMethod,
        startCheckout: startCheckout,
        completeEmbeddedPayment: completeEmbeddedPayment,
        currentPayment: currentPayment,
        applyCoupon: applyCoupon,
        removeCoupon: removeCoupon,
        clearServerError: clearServerError,
        serverErrors: serverErrors,
        isProductQuantityIncreaseDisabled,
        isShopUnlocked,
        isProductQuantityIncreaseDisabledByShopUnlock,
        getShopUnlockProductLimit,
        getCartState,
    };

    return (
        <CartContext.Provider value={contextValue}>
            {children}
        </CartContext.Provider>
    );
};

// Custom hook to use order context
export const useCart = () => {
    const context = useContext(CartContext);
    if (context === null) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
